<template>
  <el-form :model="form" :rules="rules" ref="form" label-position="top" @submit.prevent.native="onSubmit">
    <app-placeholder-horizontal :imageId="form.imageId" @upload="onUpload" />
    <el-form-item label="Recipe title" prop="title">
      <el-input v-model="form.title"></el-input>
    </el-form-item>
    <el-form-item label="Description in 280 characters" prop="desc">
      <el-input type="textarea" v-model="form.desc" rows="5"></el-input>
    </el-form-item>
    <el-form-item label="Alternate names (separate with comma)">
      <el-input v-model="form.alternateNames"></el-input>
    </el-form-item>
    <el-form-item label="Background" prop="content">
      <el-input id="mde" type="textarea" v-model="form.content"></el-input>
    </el-form-item>
    <el-form-item label="Key ingredient">
      <el-select v-model="form.keyIngredient" placeholder="Select key ingredient used">
        <el-option v-for="(id, name) in keyIngredients" :key="id" :label="name" :value="id"></el-option>
      </el-select>
    </el-form-item>
    <div class="row">
      <div class="col-4">
        <el-form-item label="Type of dish" prop="dishTypes">
          <el-select v-model="form.dishTypes" multiple placeholder="Select dish types">
            <el-option v-for="(id, name) in dishTypes" :key="id" :label="name" :value="id"></el-option>
          </el-select>
        </el-form-item>
      </div>
      <div class="col-4">
        <el-form-item label="Cuisines" prop="cuisines">
          <el-select v-model="form.cuisines" multiple collapse-tags placeholder="Select applicable cuisines">
            <el-option v-for="(id, name) in cuisines" :key="id" :label="name" :value="id"></el-option>
          </el-select>
        </el-form-item>
      </div>
      <div class="col-4">
        <el-form-item label="SEO Category" prop="category">
          <el-select v-model="form.category" placeholder="Select a category">
            <el-option v-for="category in categories" :key="category.slug" :label="category.title" :value="category.slug"></el-option>
          </el-select>
        </el-form-item>
      </div>
    </div>
    <div class="row">
      <div class="col-6">
        <el-form-item label="YouTube video ID">
          <el-input v-model="form.youtube"></el-input>
        </el-form-item>
      </div>
      <div class="col-6">
        <el-form-item label="Servings" prop="servings">
          <el-input-number v-model="form.servings" :min="1" :max="100"></el-input-number>
        </el-form-item>
      </div>
    </div>
    <div class="row">
      <div class="col-4">
        <el-form-item label="Preparation (mins)" prop="durationPrep">
          <el-input v-model="form.durationPrep"></el-input>
        </el-form-item>
      </div>
      <div class="col-4">
        <el-form-item label="Cooking (mins)" prop="durationCook">
          <el-input v-model="form.durationCook"></el-input>
        </el-form-item>
      </div>
      <div class="col-4">
        <el-form-item label="Total time">
          <el-input v-model="totalTime" placeholder="00:00" disabled></el-input>
        </el-form-item>
      </div>
    </div>
    <el-button native-type="submit" type="primary" :loading="loading">Save</el-button>
  </el-form>
</template>

<script>
import SimpleMDE from 'simplemde'
import AppPlaceholderHorizontal from '@/components/App/AppPlaceholderHorizontal'

export default {
  components: {
    AppPlaceholderHorizontal,
  },

  props: {
    recipe: {
      type: Object,
    },
  },

  data() {
    return {
      loading: false,
      form: {
        title: this.recipe.title,
        youtube: this.recipe.youtube.video_id,
        alternateNames: this.recipe.alternate_names.join(', ') || '',
        category: this.recipe.category,
        dishTypes: this.recipe.dish_types,
        cuisines: this.recipe.cuisines,
        imageId: this.recipe.image_id,
        keyIngredient: this.recipe.key_ingredient,
        durationPrep: this.recipe.duration.prep,
        durationCook: this.recipe.duration.cook,
        servings: this.recipe.servings,
        desc: this.recipe.desc,
        content: this.recipe.content,
        cuisines: this.recipe.cuisines,
      },
      rules: {
        'title': [
          { required: true, message: 'Enter the recipe title.', trigger: 'blur' },
          { max: 255, message: 'Title should not exceed 255 characters.', trigger: 'blur' },
        ],
        'desc': [
          { required: true, message: 'Enter the recipe description.', trigger: 'blur' },
          { max: 280, message: 'Description should not exceed 280 characters.', trigger: 'blur' },
        ],
        'dishTypes': [
          { required: true, message: 'Select the dish types.', trigger: 'blur' },
        ],
        'content': [
          { required: true, message: 'Enter the recipe content.', trigger: 'blur' },
        ],
        'servings': [
          { required: true, message: 'Enter the servings.', trigger: 'blur' },
        ],
        'durationPrep': [
          { required: true, message: 'Enter the preparation time.', trigger: 'blur' },
        ],
        'durationCook': [
          { required: true, message: 'Enter the cooking time.', trigger: 'blur' },
        ],
        'category': [
          { required: true, message: 'Select a category.', trigger: 'blur' },
        ],
        'cuisines': [
          { required: true, message: 'Select the cuisines of this recipe.', trigger: 'blur' },
        ],
      },
      cuisines: {
        'American': 'American',
        'American Chinese': 'American Chinese',
        'Bruneian': 'Bruneian',
        'Burmese': 'Burmese',
        'Cambodian': 'Cambodian',
        'Chinese': 'Chinese',
        'Cantonese': 'Cantonese',
        'East Timorese': 'East Timorese',
        'Filipino': 'Filipino',
        'Indian': 'Indian',
        'Indonesian': 'Indonesian',
        'Japanese': 'Japanese',
        'Korean': 'Korean',
        'Laotian': 'Laotian',
        'Macanese': 'Macanese',
        'Malay': 'Malay',
        'Malaysian': 'Malaysian',
        'Pakistani': 'Pakistani',
        'Pan-Asian': 'Pan-Asian',
        'Peranakan': 'Peranakan',
        'Singaporean': 'Singaporean',
        'Taiwanese': 'Taiwanese',
        'Thai': 'Thai',
        'Vietnamese': 'Vietnamese',
      },
      dishTypes: {
        'Appetizer': 'Appetizer',
        'Basics': 'Basics',
        'Bread': 'Bread',
        'Condiment': 'Condiment',
        'Dessert': 'Dessert',
        'Drink': 'Drink',
        'Main': 'Main',
        'Side': 'Side',
        'Snack': 'Snack',
        'Soup':'Soup',
      },
      keyIngredients: {
        'Not Applicable': null,
        'Beef': 'Beef',
        'Chicken': 'Chicken',
        'Fish': 'Fish',
        'Mutton': 'Mutton',
        'Pork': 'Pork',
        'Shellfish': 'Shellfish',
        'Vegetable': 'Vegetable',
      },
      categories: [
        { slug: 'meat', title: 'Meat' },
        { slug: 'seafood', title: 'Seafood' },
        { slug: 'vegetables', title: 'Vegetables' },
        { slug: 'rice-and-noodles', title: 'Rice and Noodles' },
        { slug: 'egg', title: 'Egg' },
        { slug: 'drinks', title: 'Drinks' },
        { slug: 'soups', title: 'Soups' },
        { slug: 'desserts', title: 'Desserts and Kuihs' },
        { slug: 'condiments', title: 'Condiments and Others' },
      ],
    }
  },

  computed: {
    totalTime() {
      return Number(this.form.durationPrep) + Number(this.form.durationCook)
    }
  },

  mounted() {
    var mde = new SimpleMDE({ element: document.getElementById('mde') })
    mde.codemirror.on('change', () => {
      this.form.content = mde.value()
    })
  },

  methods: {
    async onSubmit() {
      this.$refs['form'].validate(async valid => {
        if (!valid) {
          return false
        }

        this.loading = true

        try {
          var { data } = await this.$http.put(`/recipes/${ this.$route.params.id }`, {
            title: this.form.title,
            youtube: this.form.youtube,
            alternate_names: this.form.alternateNames,
            category: this.form.category,
            dish_types: this.form.dishTypes,
            key_ingredient: this.form.keyIngredient,
            prep_time: this.form.durationPrep,
            cook_time: this.form.durationCook,
            servings: this.form.servings,
            desc: this.form.desc,
            content: this.form.content,
            cuisines: this.form.cuisines,
          })
        } catch(err) {
          this.$message.error(err.response.data.message)
          return false
        } finally {
          this.loading = false
        }

        this.$message.success(data.message)
      })
    },

    async onUpload(imageId) {
      try {
        var { data } = await this.$http.put(`/recipes/${ this.$route.params.id }`, {
          image_id: imageId,
          orientation: 'h',
        })
      } catch(err) {
        this.$message.error(err.response.data.message)
        return false
      }

      this.$message.success('The recipe has been updated.')
    },

    setCountries(countries) {
      this.form.countries = countries
    },
  },
}
</script>
